import type { DateArg as DateFnsDateArg } from "date-fns";
import * as dateFns from "date-fns";
import { ja } from "date-fns/locale";
import * as tzDateFns from "date-fns-tz";

const TIMEZONES = {
  JST: "Asia/Tokyo",
} as const;

const FORMAT_TEMPLATES = {
  ISO_DATE: "yyyy-MM-dd",
  ISO_DATE_TIME: "yyyy-MM-dd'T'HH:mm:ssXXX",
  MONTH_WITH_YEAR: "yyyy年MM月",
  SHORT_DATE: "MM月dd日",
  SHORT_DATE_TIME: "MM月dd日 HH:mm",
  SHORT_DATE_WITH_WEEKDAY: "MM月dd日 (EEE)",
  SHORT_DATE_WITH_YEAR: "yyyy年MM月dd日",
} as const;

type DateArg = DateFnsDateArg<Date>;

function jst(date: DateArg) {
  return tzDateFns.toZonedTime(date, TIMEZONES.JST);
}

export function startOfDay(date: DateArg) {
  return dateFns.startOfDay(jst(date));
}

export function startOfWeek(date: DateArg) {
  // 週の開始日はロケールに依存させる（日本語ロケールを明示）
  return dateFns.startOfWeek(jst(date));
}

export function startOfMonth(date: DateArg) {
  return dateFns.startOfMonth(jst(date));
}

export function endOfDay(date: DateArg) {
  return dateFns.endOfDay(jst(date));
}

export function endOfWeek(date: DateArg) {
  // 週の終了日もロケールに依存させる（日本語ロケールを明示）
  return dateFns.endOfWeek(jst(date));
}

export function endOfMonth(date: DateArg) {
  return dateFns.endOfMonth(jst(date));
}

function format(date: DateArg, formatStr: (typeof FORMAT_TEMPLATES)[keyof typeof FORMAT_TEMPLATES]) {
  return tzDateFns.format(jst(date), formatStr, { locale: ja, timeZone: TIMEZONES.JST });
}

export function formatShortDate(date: DateArg) {
  return format(jst(date), FORMAT_TEMPLATES.SHORT_DATE);
}

export function formatShortDateTime(date: DateArg) {
  return format(jst(date), FORMAT_TEMPLATES.SHORT_DATE_TIME);
}

export function formatShortDateWithWeekday(date: DateArg) {
  return format(jst(date), FORMAT_TEMPLATES.SHORT_DATE_WITH_WEEKDAY);
}

export function formatShortDateWithYear(date: DateArg) {
  return format(jst(date), FORMAT_TEMPLATES.SHORT_DATE_WITH_YEAR);
}

export function formatMonthWithYear(date: DateArg) {
  return format(jst(date), FORMAT_TEMPLATES.MONTH_WITH_YEAR);
}

export function getHours(date: DateArg) {
  return dateFns.getHours(jst(date));
}

export function getMinutes(date: DateArg) {
  return dateFns.getMinutes(jst(date));
}

export function getSeconds(date: DateArg) {
  return dateFns.getSeconds(jst(date));
}

export function parseFloatTime(date: DateArg) {
  const hours = getHours(date);
  const minutes = getMinutes(date);
  const seconds = getSeconds(date);
  const value = Math.round(((hours * 60 + minutes + seconds / 60) / 60) * 100) / 100;
  return Math.min(23.99, value);
}

export function addDays(date: DateArg, days: number) {
  return dateFns.addDays(jst(date), days);
}

export function subDays(date: DateArg, days: number) {
  return dateFns.subDays(jst(date), days);
}

export function getDay(date: DateArg) {
  return dateFns.getDay(jst(date));
}

export function nextDay(date: DateArg) {
  return addDays(date, 1);
}

export function prevDay(date: DateArg) {
  return subDays(date, 1);
}

export function prevMonth(date: DateArg) {
  return dateFns.subMonths(jst(date), 1);
}

export function nextWeekEndDay(date: DateArg) {
  return addDays(endOfWeek(date), 7);
}

export function prevWeekEndDay(date: DateArg) {
  return subDays(endOfWeek(date), 7);
}

export function isToday(date: DateArg) {
  return isSameDay(date, jst(new Date()));
}

export function isSameDay(laterDate: DateArg, earlierDate: DateArg, options?: dateFns.IsSameDayOptions) {
  return dateFns.isSameDay(jst(laterDate), jst(earlierDate), options);
}

export function isBefore(laterDate: DateArg, earlierDate: DateArg) {
  return dateFns.isBefore(jst(laterDate), jst(earlierDate));
}

export function isSameOrBefore(laterDate: DateArg, earlierDate: DateArg, options?: dateFns.IsSameDayOptions) {
  return isSameDay(laterDate, earlierDate, options) || isBefore(laterDate, earlierDate);
}

export function isAfter(laterDate: DateArg, earlierDate: DateArg) {
  return dateFns.isAfter(jst(laterDate), jst(earlierDate));
}

export function isSameOrAfter(laterDate: DateArg, earlierDate: DateArg, options?: dateFns.IsSameDayOptions) {
  return isSameDay(laterDate, earlierDate, options) || isAfter(laterDate, earlierDate);
}

export function formatISODateTimeString(date: DateArg) {
  return format(jst(date), FORMAT_TEMPLATES.ISO_DATE_TIME);
}

export function formatISODateString(date: DateArg) {
  return format(jst(date), FORMAT_TEMPLATES.ISO_DATE);
}

export function differenceInDays(laterDate: DateArg, earlierDate: DateArg) {
  return dateFns.differenceInDays(jst(laterDate), jst(earlierDate));
}

export function differenceInMinutes(laterDate: DateArg, earlierDate: DateArg) {
  return dateFns.differenceInMinutes(jst(laterDate), jst(earlierDate));
}

export function isCurrentWeek(date: DateArg) {
  const today = new Date();
  const weekStart = startOfWeek(date);
  const weekEnd = endOfWeek(date);
  return dateFns.isAfter(jst(today), weekStart) && dateFns.isBefore(jst(today), weekEnd);
}
